import localApiAdapter from '../../core/services/localApiAdapter';

/** Fired after UOM create/update/delete so product/service dialogs reload units. */
export const CATALOG_UNITS_REFRESH_EVENT = 'catalog-units-changed';

export function dispatchCatalogUnitsRefresh() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CATALOG_UNITS_REFRESH_EVENT));
  }
}

/** Alias groups for matching catalog unit codes ↔ UOM symbols/names. */
export const UOM_ALIAS_GROUPS = [
  ['pcs', 'piece', 'pièce', 'unite', 'قطعة'],
  ['kg', 'kilogram', 'kilogramme', 'كيلوغرام'],
  ['g', 'gram', 'غرام'],
  ['t', 'ton', 'tonne', 'طن'],
  ['l', 'liter', 'litre', 'لتر'],
  ['ml', 'milliliter'],
  ['m', 'meter', 'metre', 'متر'],
  ['m²', 'm2'],
  ['ctn', 'carton', 'كرتون'],
  ['pkt', 'paquet', 'pack', 'باكت', 'باكيت'],
  ['sac', 'bag', 'كيس'],
  ['h', 'heure', 'hour', 'ساعة'],
  ['minute', 'min', 'دقيقة'],
  ['jour', 'day', 'يوم'],
  ['mois', 'month', 'شهر'],
  ['semaine', 'week', 'أسبوع'],
  ['session', 'جلسة'],
  ['intervention', 'تدخل', 'زيارة'],
  ['forfait', 'مقابل ثابت'],
  ['contrat', 'عقد'],
  ['box', 'صندوق'],
  ['douzaine', 'dozen', 'دزينة'],
  ['set', 'طقم'],
  ['lot', 'دفعة'],
  ['paire', 'pair', 'زوج'],
  ['palette', 'pallet', 'بالتة'],
  ['rouleau', 'roll', 'لفة'],
  ['bouteille', 'bottle', 'زجاجة'],
  ['bigbag', 'كيس كبير'],
];

export function buildUomMatchKeys(unit) {
  const keys = new Set();
  const add = (value) => {
    if (value == null || value === '') return;
    keys.add(String(value).trim().toLowerCase());
  };

  add(unit?.symbol);
  add(unit?.id);
  add(unit?.unit_number);
  add(unit?.name);

  const symbol = String(unit?.symbol || '').trim().toLowerCase();
  for (const group of UOM_ALIAS_GROUPS) {
    const normalized = group.map((entry) => String(entry).trim().toLowerCase());
    if (normalized.includes(symbol)) {
      normalized.forEach(add);
    }
  }

  return keys;
}

function buildCatalogUnitCountMap(items) {
  const map = new Map();
  for (const item of items || []) {
    const raw = item?.unit;
    if (raw == null || String(raw).trim() === '') continue;
    const key = String(raw).trim().toLowerCase();
    map.set(key, (map.get(key) || 0) + 1);
  }
  return map;
}

function sumUsageForKeys(map, keys) {
  let total = 0;
  for (const key of keys) {
    if (map.has(key)) total += map.get(key);
  }
  return total;
}

/** Client-side usage enrichment when API has not computed counts yet. */
export function enrichUnitsWithCatalogUsage(units, products, services) {
  const productMap = buildCatalogUnitCountMap(products);
  const serviceMap = buildCatalogUnitCountMap(services);

  return (units || []).map((unit) => {
    const keys = buildUomMatchKeys(unit);
    const usage_product_count = sumUsageForKeys(productMap, keys);
    const usage_service_count = sumUsageForKeys(serviceMap, keys);
    const usage_total = usage_product_count + usage_service_count;

    return {
      ...unit,
      usage_product_count,
      usage_service_count,
      usage_count: usage_total,
      product_count: usage_total,
    };
  });
}

function unwrapListResponse(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

export async function fetchCatalogUnits(scope) {
  try {
    const scopedUrl = scope
      ? `/api/articles/unites-mesure?status=actif&scope=${scope}`
      : '/api/articles/unites-mesure?status=actif';
    let rows = unwrapListResponse(await localApiAdapter.get(scopedUrl));

    if (rows.length === 0 && scope) {
      const allRows = unwrapListResponse(await localApiAdapter.get('/api/articles/unites-mesure?status=actif'));
      rows = allRows.filter((u) => (u.unit_scope || 'product') === scope);
    }

    return rows;
  } catch (error) {
    console.error(`Error loading ${scope} units:`, error);
    return [];
  }
}

export async function fetchAllCatalogUnitsWithUsage() {
  try {
    const [uomRaw, productsRaw, servicesRaw] = await Promise.all([
      localApiAdapter.get('/api/articles/unites-mesure?status=actif'),
      localApiAdapter.get('/api/products?type=product'),
      localApiAdapter.get('/api/products?type=service'),
    ]);

    let units = unwrapListResponse(uomRaw);
    const products = unwrapListResponse(productsRaw);
    const services = unwrapListResponse(servicesRaw);

    const hasApiUsage = units.some((u) => 'usage_product_count' in u);
    if (!hasApiUsage) {
      units = enrichUnitsWithCatalogUsage(units, products, services);
    }

    return { units, products, services };
  } catch (error) {
    console.error('Error loading units with usage:', error);
    return { units: [], products: [], services: [] };
  }
}

export function getUomOptionValue(uom) {
  const sym = uom?.symbol != null ? String(uom.symbol).trim() : '';
  if (sym) return sym;
  return uom?.id || '';
}

export function formatUomSelectLabel(uom) {
  const name = uom?.name != null ? String(uom.name).trim() : '';
  const sym = uom?.symbol != null ? String(uom.symbol).trim() : '';
  if (name && sym) return `${name} (${sym})`;
  return name || sym || uom?.unit_number || uom?.id || '';
}

export function unitValueMatchesUom(unitValue, uom) {
  const raw = String(unitValue || '').trim().toLowerCase();
  if (!raw) return false;
  return buildUomMatchKeys(uom).has(raw);
}

export function collectOrphanUnitValues(catalogItems, uomRecords) {
  const seen = new Set();
  const orphans = [];

  for (const item of catalogItems || []) {
    const value = String(item?.unit || '').trim();
    if (!value) continue;
    const lower = value.toLowerCase();
    if (seen.has(lower)) continue;

    const matched = (uomRecords || []).some((uom) => unitValueMatchesUom(value, uom));
    if (!matched) {
      seen.add(lower);
      orphans.push(value);
    }
  }

  return orphans.sort((a, b) => a.localeCompare(b, 'ar'));
}

export function buildUnitSelectOptionsFromRecords(records, {
  orphanValues = [],
  orphanSuffix = '',
  resolveOrphanLabel,
} = {}) {
  const options = (records || []).map((uom) => ({
    value: getUomOptionValue(uom),
    label: formatUomSelectLabel(uom),
    id: uom.id,
    symbol: uom.symbol,
    name: uom.name,
    fromUom: true,
  }));

  const seen = new Set(options.map((o) => String(o.value).trim().toLowerCase()));

  for (const raw of orphanValues) {
    const value = String(raw).trim();
    if (!value) continue;
    const lower = value.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    const baseLabel = resolveOrphanLabel ? resolveOrphanLabel(value) : value;
    options.push({
      value,
      label: orphanSuffix ? `${baseLabel} ${orphanSuffix}` : baseLabel,
      orphan: true,
    });
  }

  return options;
}

export function resolveUnitLabelFromOptions(unit, options, t, { emptyLabel, fallbackResolver } = {}) {
  const raw = unit == null ? '' : String(unit).trim();
  if (!raw) return emptyLabel ?? '';
  const lower = raw.toLowerCase();
  const hit = options.find(
    (o) => o.value === raw
      || o.id === raw
      || (o.symbol && String(o.symbol).trim().toLowerCase() === lower)
      || (o.value && String(o.value).trim().toLowerCase() === lower),
  );
  if (hit?.label) return hit.label;
  return fallbackResolver ? fallbackResolver(unit, t) : raw;
}

/** Unit codes for physical / stock products (legacy label lookup only). */
export const PRODUCT_UNIT_CODES = [
  'pcs', 'unite', 'lot', 'paire', 'set', 'douzaine',
  'kg', 'g', 'tonne', 'liter', 'ml', 'cl',
  'meter', 'cm', 'mm', 'km', 'm2', 'm3',
  'box', 'carton', 'palette', 'paquet', 'rouleau', 'bouteille', 'sac', 'bigbag',
];

/** Unit codes for services (legacy label lookup only). */
export const SERVICE_UNIT_CODES = [
  'minute', 'heure', 'demi_journee', 'jour', 'semaine', 'mois', 'annee',
  'session', 'intervention', 'forfait', 'contrat', 'unite',
];

const PRODUCT_UNIT_ALIASES = {
  piece: 'pcs',
  pièce: 'pcs',
  'قطعة': 'pcs',
  unit: 'unite',
  'الوحدة': 'unite',
  batch: 'lot',
  pair: 'paire',
  dozen: 'douzaine',
  kilogram: 'kg',
  kilogramme: 'kg',
  'كيلوغرام': 'kg',
  gram: 'g',
  'غرام': 'g',
  ton: 'tonne',
  'طن': 'tonne',
  litre: 'liter',
  l: 'liter',
  'لتر': 'liter',
  milliliter: 'ml',
  centiliter: 'cl',
  metre: 'meter',
  m: 'meter',
  'متر': 'meter',
  centimeter: 'cm',
  millimeter: 'mm',
  kilometer: 'km',
  'متر مربع': 'm2',
  'm²': 'm2',
  'متر مكعب': 'm3',
  'm³': 'm3',
  'صندوق': 'box',
  'كرتون': 'carton',
  ctn: 'carton',
  pallet: 'palette',
  pack: 'paquet',
  roll: 'rouleau',
  bottle: 'bouteille',
  'زجاجة': 'bouteille',
  bag: 'sac',
  'كيس': 'sac',
  'ظرف': 'sac',
  'باكت': 'paquet',
  pkt: 'paquet',
};

const SERVICE_UNIT_ALIASES = {
  min: 'minute',
  'دقيقة': 'minute',
  hour: 'heure',
  h: 'heure',
  'ساعة': 'heure',
  'نصف يوم': 'demi_journee',
  day: 'jour',
  'يوم': 'jour',
  week: 'semaine',
  'أسبوع': 'semaine',
  month: 'mois',
  'شهر': 'mois',
  year: 'annee',
  'سنة': 'annee',
  'جلسة': 'session',
  visit: 'intervention',
  'تدخل': 'intervention',
  'زيارة': 'intervention',
  flat: 'forfait',
  'مقابل ثابت': 'forfait',
  contract: 'contrat',
  'عقد': 'contrat',
};

function normalizeCode(raw, allowed, aliases, fallback) {
  const s = raw == null ? '' : String(raw).trim();
  if (!s) return fallback;
  const lower = s.toLowerCase();
  if (allowed.includes(lower)) return lower;
  if (allowed.includes(s)) return s;
  const alias = aliases[lower] ?? aliases[s];
  if (alias && allowed.includes(alias)) return alias;
  return fallback;
}

export function normalizeProductUnitCode(raw, fallback = '') {
  return normalizeCode(raw, PRODUCT_UNIT_CODES, PRODUCT_UNIT_ALIASES, fallback);
}

export function normalizeServiceUnitCode(raw, fallback = '') {
  return normalizeCode(raw, SERVICE_UNIT_CODES, SERVICE_UNIT_ALIASES, fallback);
}

const PRODUCT_UNIT_AR = {
  pcs: 'قطعة',
  unite: 'وحدة',
  lot: 'دفعة',
  paire: 'زوج',
  set: 'طقم',
  douzaine: 'دزينة',
  kg: 'كيلوغرام',
  g: 'غرام',
  tonne: 'طن',
  liter: 'لتر',
  ml: 'مل',
  cl: 'سنتilitre',
  meter: 'متر',
  cm: 'سم',
  mm: 'مليمتر',
  km: 'كيلومتر',
  m2: 'm²',
  m3: 'm³',
  box: 'صندوق',
  carton: 'كرتون',
  palette: 'بالتة',
  paquet: 'حزمة',
  rouleau: 'لفة',
  bouteille: 'زجاجة',
  sac: 'كيس',
  bigbag: 'كيس كبير',
};

const SERVICE_UNIT_AR = {
  minute: 'دقيقة',
  heure: 'ساعة',
  demi_journee: 'نصف يوم',
  jour: 'يوم',
  semaine: 'أسبوع',
  mois: 'شهر',
  annee: 'سنة',
  session: 'جلسة',
  intervention: 'تدخل',
  forfait: 'مقابل ثابت',
  contrat: 'عقد',
  unite: 'بالوحدة',
};

export function resolveProductUnitLabel(unit, t, { emptyLabel } = {}) {
  const code = normalizeProductUnitCode(unit, '');
  if (!code) {
    return emptyLabel ?? t('modules.article.pages.products.units.select');
  }
  const key = `modules.article.pages.products.units.${code}`;
  const translated = t(key);
  if (translated !== key) return translated;
  return PRODUCT_UNIT_AR[code] || String(unit);
}

export function resolveServiceUnitLabel(unit, t) {
  const code = normalizeServiceUnitCode(unit, '');
  if (!code) return String(unit || '');
  const key = `modules.article.pages.services.units.${code}`;
  const translated = t(key);
  if (translated !== key) return translated;
  return SERVICE_UNIT_AR[code] || String(unit);
}

/** Arabic label for catalog unit codes (UOM orphans, legacy French codes). */
export function resolveCatalogUnitLabelArabic(value, t) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  const serviceCode = normalizeServiceUnitCode(raw, '');
  if (serviceCode && SERVICE_UNIT_AR[serviceCode]) {
    const key = `modules.article.pages.services.units.${serviceCode}`;
    const translated = t(key);
    if (translated !== key) return translated;
    return SERVICE_UNIT_AR[serviceCode];
  }

  const productCode = normalizeProductUnitCode(raw, '');
  if (productCode && PRODUCT_UNIT_AR[productCode]) {
    const key = `modules.article.pages.products.units.${productCode}`;
    const translated = t(key);
    if (translated !== key) return translated;
    return PRODUCT_UNIT_AR[productCode];
  }

  const serviceLabel = resolveServiceUnitLabel(raw, t);
  if (serviceLabel && serviceLabel !== raw) return serviceLabel;

  const productLabel = resolveProductUnitLabel(raw, t, { emptyLabel: '' });
  const selectKey = t('modules.article.pages.products.units.select');
  if (productLabel && productLabel !== raw && productLabel !== selectKey) {
    return productLabel;
  }

  return raw;
}

/** @deprecated Use buildUnitSelectOptionsFromRecords with UOM API records instead. */
export function buildProductUnitSelectOptions(t) {
  return PRODUCT_UNIT_CODES.map((value) => ({
    value,
    label: resolveProductUnitLabel(value, t, { emptyLabel: value }),
  }));
}

/** @deprecated Use buildUnitSelectOptionsFromRecords with UOM API records instead. */
export function buildServiceUnitSelectOptions(t) {
  return SERVICE_UNIT_CODES.map((value) => ({
    value,
    label: resolveServiceUnitLabel(value, t),
  }));
}

export function defaultUnitFromOptions(options, fallback = '') {
  const first = options?.find((o) => o.fromUom)?.value ?? options?.[0]?.value;
  return first || fallback;
}
